import { Divider, Tab, Tabs, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { useLocation, useSearchParams } from '@remix-run/react';
import { chunk } from 'lodash';
import * as React from 'react';
import { Image } from 'remix-image';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { Product, ProductsResponse } from '~/services/api.server';
import { ProductTypes } from '~/utils/constants';
import { DESIGN_STATUS_PARAM, PAGE_PARAM, TYPES_PARAM } from '~/utils/searchParams';
import type { CategoryNode } from '~/utils/shop';
import { defaultSortValue, productsPerPage, productsSortOptions } from '~/utils/shop';
import Pagination from '../shared/Pagination';
import ProductCard from '../shared/ProductCard';
import { SortBy } from '../shared/SortBy';
import { MuiRemixLink } from '../shared/links';
import { SearchText } from './SearchText';
import ShopCarousel, { ShopCarouselSlide } from './ShopCarousel';
import { EmptyState } from './common';

export type ProductsGridProps = {
  products: ProductsResponse;
  path?: string;
  dense?: boolean;
  selectedCategory?: CategoryNode | undefined;
  renderCarousel?: boolean;
  renderProduct?: (p: Product) => React.ReactNode;
  isDashboard?: boolean;
};

type ProductsGridInnerProps = ProductsGridProps & {
  children?: React.ReactNode;
};

const packCarouselContent = {
  type: 'carousel',
  slides: [
    {
      eyebrow: 'Onboarding Swag Packs',
      description: "Our swag packs are the perfect way to show your new hires that they're part of the team.",
      buttonText: 'Slide to see offers',
      buttonLink: null,
      imageUrl: { desktop: 'landing/shop/banner-desktop-8.png', mobile: 'landing/shop/banner-mobile-3.png' },
      title: null,
    },
    {
      eyebrow: 'Marketing Swag Packs',
      title: null,
      buttonText: 'Slide to see offers',
      buttonLink: null,
      imageUrl: { desktop: 'landing/shop/banner-desktop-7.png', mobile: 'landing/shop/banner-mobile-4.png' },
      description: 'Make a great first impression with our ready to go and fully customizable swag packs.',
    },
    {
      eyebrow: 'Loyalty Swag Packs',
      description: 'Make your employees feel like valued members of the team with our swag packs.',
      buttonText: 'Slide to see offers',
      buttonLink: null,
      imageUrl: { desktop: 'landing/shop/banner-desktop-6.png', mobile: 'landing/shop/banner-mobile-5.png' },
      title: null,
    },
  ],
};

const bulkCarouselContent = {
  type: 'carousel',
  slides: [
    {
      eyebrow: 'Drinkware',
      description: 'Stay hydrated and on-brand with our custom drinkware collection.',
      buttonText: 'Slide to see offers',
      buttonLink: null,
      imageUrl: { desktop: 'landing/shop/shop-banner-bulk-page-1.png', mobile: 'landing/shop/banner-mobile.png' },
      title: null,
    },
    {
      eyebrow: 'Totes & Duffles',
      description: 'Carry your essentials in style with our collection of totes and duffles.',
      buttonText: 'Slide to see offers',
      buttonLink: null,
      imageUrl: { desktop: 'landing/shop/shop-banner-bulk-page-2.png', mobile: 'landing/shop/banner-mobile-1.png' },
      title: null,
    },
    {
      eyebrow: 'Hoodies',
      description: 'Elevate your brand awareness with our collection of custom hoodies.',
      buttonText: 'Slide to see offers',
      buttonLink: null,
      imageUrl: { desktop: 'landing/shop/shop-banner-bulk-page-3.png', mobile: 'landing/shop/banner-mobile-2.png' },
      title: null,
    },
  ],
};

export default function ProductsGrid({
  children,
  products,
  path,
  renderCarousel,
  selectedCategory,
  dense = false,
  isDashboard = false,
  renderProduct = (p: Product) => <ProductCard isDashboard={isDashboard} key={p.id} product={p} />,
}: ProductsGridInnerProps) {
  const [query, setSearchParams] = useSearchParams();
  // Const created to conditionally render grid carousels only in /shop
  const location = useLocation();
  const isBulkShop = location.pathname.includes('/shop/bulk-swag');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const page = parseInt(query.get(PAGE_PARAM) || '1', 10);
  const search = query.get('search');

  const shouldShowPagination = products.results?.length > 0 && products.count > productsPerPage;

  const firstProductCount = products.count === 0 ? 0 : page === 1 ? 1 : (page - 1) * productsPerPage + 1;
  const lastProductCount = Math.min(page * productsPerPage, products.count);

  const carouselSubcategories = selectedCategory?.subcategories?.map(
    (subcategory: any): Omit<CategoryCardProps, 'width'> => {
      return {
        title: subcategory.name,
        buttonLink: subcategory.href || subcategory.slug,
        orientation: 'vertical',
        imageUrl: subcategory.image || NoImageAvailable,
      };
    }
  );

  return (
    <Box>
      {renderCarousel && (
        <MainCarousel
          mainSlides={isBulkShop ? bulkCarouselContent.slides : packCarouselContent.slides}
          secondarySlides={carouselSubcategories as CategoryCardProps[]}
        />
      )}

      {search && isDashboard && <SearchText path={location.pathname} />}

      {isDashboard && (
        <Stack
          width="100%"
          direction={{ md: 'row' }}
          justifyContent="space-between"
          alignItems={{ md: 'center' }}
          mb={2}
        >
          <Tabs
            value={(() => {
              const typesParams = query.getAll(TYPES_PARAM);
              if (typesParams.includes(ProductTypes.CUSTOM_PACK_TEMPLATE.toString())) {
                return 'custom-templates';
              } else {
                return 'custom-products';
              }
            })()}
            onChange={(e, value) => {
              query.delete(TYPES_PARAM);
              if (value === 'custom-templates') {
                if (query.has(DESIGN_STATUS_PARAM)) {
                  query.delete(DESIGN_STATUS_PARAM);
                }
                query.append(TYPES_PARAM, ProductTypes.CUSTOM_PACK_TEMPLATE.toString());
              } else {
                query.append(TYPES_PARAM, ProductTypes.CUSTOM_PACK.toString());
                query.append(TYPES_PARAM, ProductTypes.CUSTOM_PRODUCT.toString());
              }

              if (query.has(PAGE_PARAM)) {
                query.set(PAGE_PARAM, '1');
              }

              setSearchParams(query);
            }}
          >
            <Tab value={'custom-products'} label="My products" />
            <Tab value={'custom-templates'} label="My custom templates" />
          </Tabs>
          <Typography variant="body2" color="#12123D99" mt={{ xs: 2, md: 0 }}>
            {firstProductCount}-{lastProductCount} of {products.count} items
          </Typography>
        </Stack>
      )}

      {!renderCarousel && !isDashboard && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          sx={(theme) => ({
            pb: 3,
            [theme.breakpoints.down(425)]: {
              flexDirection: 'column',
              gap: 1,
            },
          })}
        >
          {children}

          <Grid container alignItems="center" flex={1} width="fit-content">
            <Typography
              variant="body2"
              sx={{
                color: (theme) => theme.palette.text.primary,
                display: { xs: 'none', sm: 'block' },
                mr: search ? 0.5 : 0,
              }}
            >
              {products.count <= productsPerPage
                ? ''
                : `${firstProductCount} - ${lastProductCount} of ${products.count} items${search ? ' - ' : ''}`}
            </Typography>
            <SearchText path={path} />
          </Grid>

          <Typography variant="body2" mr={2} color="text.secondary">
            Prices based on 100 items
          </Typography>
          <SortBy options={productsSortOptions} defaultOption={defaultSortValue} />
        </Box>
      )}
      {!isMobile && !isDashboard && <Divider sx={{ my: { md: 3 } }} />}
      {renderCarousel && !isDashboard && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            sx={{
              pb: { xs: 1, md: 3 },
              // SortBy Custom Styles
              '.MuiOutlinedInput-notchedOutline': {
                border: { xs: 'none', md: '1px solid rgba(0, 0, 0, 0.23)' },
              },
            }}
          >
            {children}
            <Grid container alignItems="center" flex={1} width="fit-content">
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.text.primary,
                  display: { xs: 'none', sm: 'block' },
                  mr: search ? 0.5 : 0,
                }}
              >
                {products.count <= productsPerPage
                  ? ''
                  : `${firstProductCount} - ${lastProductCount} of ${products.count} items${search ? ' - ' : ''}`}
              </Typography>
              <SearchText path={location.pathname} />
            </Grid>
            <SortBy options={productsSortOptions} defaultOption={defaultSortValue} />
          </Box>
          {isMobile && <Divider sx={{ my: { md: 3 } }} />}
        </>
      )}

      {products.count > 0 ? (
        <Box
          display="grid"
          borderRadius={2}
          sx={[
            {
              p: isDashboard ? 0 : { xs: 1.5, sm: renderCarousel ? 0 : 2 },
              gap: { xs: 1.5, sm: 2 },
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(155px, 1fr))',
                sm: 'repeat(auto-fill, minmax(18ch, 1fr))',
                lg: 'repeat(auto-fill, minmax(25ch, 1fr))',
              },
            },
            dense && { p: { xs: 2 }, gap: { xs: 1 } },
          ]}
        >
          {products.results.map((product, i) => {
            // render banner every 8 cards
            // if (i > 1 && i % 8 === 0 && isShop) return renderGridCarouselsWithProduct(renderProduct(product), i);
            return renderProduct(product);
          })}
        </Box>
      ) : (
        <EmptyState />
      )}

      {shouldShowPagination && (
        <Stack alignItems="center" my={5}>
          <Pagination count={products.count} itemsPerPage={productsPerPage} />
        </Stack>
      )}

      <Stack></Stack>
    </Box>
  );
}

function MainCarousel({ mainSlides, secondarySlides }: { mainSlides: any[]; secondarySlides: CategoryCardProps[] }) {
  const showCategories = secondarySlides && secondarySlides.length > 0;

  const isLgScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <Container
      maxWidth={false}
      sx={{
        height: { xs: 226, md: showCategories ? 560 : 330 },
        px: { xs: 0, md: 4 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <ShopCarousel
        key="mainCarousel"
        containerSx={{ height: { xs: 226, md: 330 } }}
        indicatorContainerSx={{ bottom: isMobile ? '12px' : '60px' }}
        interval={8000}
      >
        {mainSlides?.map((slide: any, index: number) => (
          <ShopCarouselSlide sx={{ height: { xs: 226, md: 330 } }} key={`mainSlide${index}`} {...slide} />
        ))}
      </ShopCarousel>
      {showCategories && (
        <Container
          maxWidth={false}
          sx={{ width: '95%', position: 'absolute', inset: 0, top: '290px', display: { xs: 'none', md: 'block' } }}
        >
          <ShopCarousel
            key="secondaryCarousel"
            containerSx={{ height: 244 }}
            buttonSx={{ height: '48px', width: '48px' }}
            indicators={false}
            interval={8000}
            cycleNavigation={false}
            navButtonsAlwaysInvisible={secondarySlides.length <= (isLgScreen ? 3 : 4)}
          >
            {chunk(secondarySlides, isLgScreen ? 3 : 4).map((chunk, i) => (
              <Stack
                key={`chunk${i}`}
                direction="row"
                justifyContent={chunk.length < (isLgScreen ? 3 : 4) ? 'start' : 'space-between'}
                sx={{
                  width: '100%',
                  ...(chunk.length < (isLgScreen ? 3 : 4) ? { '& > div:nth-of-type(n+2)': { marginLeft: 3 } } : {}),
                }}
              >
                {chunk.map((card: CategoryCardProps) => (
                  <CategoriesCard
                    key={card.title}
                    {...card}
                    width={{ xs: `calc(25% - 8px)`, md: `calc(25% - ${chunk.length === 1 ? 0 : 24}px)` }}
                  />
                ))}
              </Stack>
            ))}
          </ShopCarousel>
        </Container>
      )}
    </Container>
  );
}

export type CategoryCardProps = {
  width: any;
  title: string;
  buttonLink: string;
  description?: string;
  orientation: string;
  imageUrl: string;
};

function CategoriesCard({ width, title, imageUrl, buttonLink }: CategoryCardProps) {
  return (
    <Grid
      item
      display="flex"
      flexDirection="column"
      px={4}
      pt={4}
      pb={2}
      height={244}
      width={width}
      bgcolor="#F0F5F9"
      alignItems="center"
      borderRadius="16px"
      overflow="hidden"
    >
      <MuiRemixLink
        href={buttonLink}
        sx={{
          textDecoration: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          height: '20%',
          mb: 4,
        }}
      >
        <Typography variant="h4" textAlign="center" fontSize={24} fontWeight={700} color="#00598B">
          {title}
        </Typography>
      </MuiRemixLink>
      <MuiRemixLink
        href={buttonLink}
        sx={{
          textDecoration: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'space-between',
          height: '60%',
          maxWidth: '100%',
        }}
      >
        <Image src={imageUrl || ''} />
      </MuiRemixLink>
    </Grid>
  );
}

// !! Product grid carousel, out of use

// function ProductGridCarousel({ title, slides }: { title: string; slides: any[] }) {
//   const isLgScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

//   return (
//     <Stack display={{ xs: 'none', md: 'flex' }} sx={{ gridColumn: '1/-1', p: { xs: 0, md: 2 } }}>
//       <Typography variant="h5" fontWeight={600} fontSize={20} color={'#00598B'} mb={2}>
//         {title}
//       </Typography>
//       <ShopCarousel containerSx={{ height: 267 }} indicators={false} height={267}>
//         {chunk(slides, isLgScreen ? 2 : 3).map((chunk, i) => {
//           return (
//             <Grid
//               key={i}
//               container
//               display={'flex'}
//               justifyContent="space-evenly"
//               height={'100%'}
//               flexWrap="nowrap"
//               bgcolor={'#F0F5F9'}
//               borderRadius="8px"
//             >
//               {chunk}
//             </Grid>
//           );
//         })}
//       </ShopCarousel>
//     </Stack>
//   );
// }

// function MobileProductGridCarousel({ title, slides }: { title: string; slides: any[] }) {
//   return (
//     <Stack display={{ xs: 'flex', md: 'none' }} sx={{ gridColumn: '1/-1', mb: 2 }}>
//       <Typography variant="h5" fontWeight={600} fontSize={16} my={2}>
//         {title}
//       </Typography>
//       <DraggableCarousel spacing={2}>{slides}</DraggableCarousel>
//     </Stack>
//   );
// }

// function renderGridCarouselsWithProduct(productComponent: React.ReactNode, i: number) {
//   return [
//     <ProductGridCarousel
//       key={i}
//       title="All To Black"
//       slides={Array(3).fill(
//         <Grid item display={'flex'} flexDirection="column" justifyContent="center" alignItems={'center'} p={3}>
//           <Image src="https://i.ibb.co/CP55jfr/1-2.png" />
//           <Button
//             variant="contained"
//             sx={{ bgcolor: '#009DC8', color: 'white', textTransform: 'uppercase', width: '80%' }}
//           >
//             Outwear
//           </Button>
//         </Grid>
//       )}
//     />,
//     <MobileProductGridCarousel
//       key={i}
//       title={'All To Black'}
//       slides={Array(3).fill(
//         <Grid
//           item
//           display={'flex'}
//           width={271}
//           height={173}
//           bgcolor="#F0F5F9"
//           justifyContent="center"
//           alignItems={'center'}
//           borderRadius={2}
//           p={2}
//         >
//           <Image src="https://i.ibb.co/CP55jfr/1-2.png" />
//           <Stack
//             display={'flex'}
//             flexDirection="column"
//             justifyContent={'space-between'}
//             alignItems="start"
//             height="100%"
//           >
//             <Typography variant="h5" fontWeight={600} color="#00598B">
//               Water Bottles
//             </Typography>
//             <Button variant="text" sx={{ color: '#009DC8', p: 0 }}>
//               DISCOVER
//             </Button>
//           </Stack>
//         </Grid>
//       )}
//     />,
//     productComponent,
//   ];
// }
